import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import type { InventoryCategory, InventoryItem, Warehouse } from '../../accounting/inventory/types/inventory.types';
import { emptyLotSelection, type LotSelection } from '../../accounting/inventory/types/lots.types';
import {
  queueProductionOrderNavigation,
  useProductionNav,
  type ProductionOrderNavigationState,
} from '../context/ProductionNavContext';
import { useProductionPermissions } from '../context/ProductionPermissionsContext';
import {
  ProductionApiError,
  cancelOrder,
  cancelTree,
  completeOrder,
  completeTree,
  createOrder,
  deleteOrder,
  getMovements,
  getOrder,
  getProducibleProducts,
  getWarehouses,
  preview,
  regenerateChildren,
  updateOrder,
} from '../services/productionService';
import { releaseOrder, unreleaseOrder } from '../services/productionOpsService';
import type {
  ProductionExtraOutputRow,
  ProductionExtraOutputType,
  ProductionInputRow,
  ProductionOutputCostMethod,
} from '../types/productionForm.types';
import type {
  CreateProductionOrderPayload,
  ProducibleProduct,
  ProductionChildPlanNode,
  ProductionMovement,
  ProductionOrder,
  ProductionPreview,
  ProductionPreviewStep,
  ProductionStockShortage,
  ProductionWarehouse,
} from '../types/production.types';
import { entryLineQuantities, sameUnitName, toBaseQty, toCalendarDate } from '../utils/productionFormat';
import { lotSelectionMismatch, payloadLots, suggestExpiry } from '../utils/productionLots';

export type ProductionEditorMode = 'new' | 'draft' | 'readonly';
/** Miktar girişi: toplam üretime göre ya da ana malzemeye göre. Fişe kaydedilmez. */
export type ProductionQuantityMode = 'total' | 'base';
type PickerKind = 'input' | ProductionExtraOutputType;

function costMethodOf(outputType: string, method?: string | null): ProductionOutputCostMethod {
  const value = (method || '').toUpperCase();
  if (value === 'SHARE' || value === 'FIXED' || value === 'NONE') return value;
  return outputType === 'COPRODUCT' ? 'SHARE' : 'NONE';
}

function isExtraOutputType(value: string): value is ProductionExtraOutputType {
  return value === 'COPRODUCT' || value === 'BYPRODUCT' || value === 'SCRAP';
}

export interface FormBanner {
  severity: 'error' | 'warning' | 'info';
  message: string;
  detail?: string;
}

interface Trigger {
  product: ProducibleProduct | null;
  qty: number | null;
  warehouseId: string;
  ops: boolean;
  routing: boolean | null;
  mode: ProductionQuantityMode;
  base: number | null;
}

function usesBase(product: ProducibleProduct | null, mode: ProductionQuantityMode): boolean {
  return mode === 'base' && Boolean(product?.baseIngredientId);
}

function parseCalendarDate(value?: string | null): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function productFromOrder(order: ProductionOrder): ProducibleProduct {
  return {
    productId: order.productId,
    name: order.productName,
    stockCode: order.stockCode,
    unit: order.unit,
    productionMode: '',
    recipeId: order.recipeId ?? '',
    recipeOutputQuantity: order.recipeOutputQuantity,
    isVariableOutput: order.isVariableOutput ?? false,
  };
}

function rowsFromOrder(order: ProductionOrder): ProductionInputRow[] {
  return (order.inputs ?? []).map((line) => ({
    key: line.id,
    productId: line.itemId,
    productName: line.itemName,
    stockCode: line.stockCode,
    unit: line.unit,
    entryUnit: line.entryUnit ?? null,
    entryFactor: line.entryFactor ?? null,
    plannedQuantity: line.plannedQuantity,
    actualQuantity: line.actualQuantity ?? line.plannedQuantity,
    warehouseId: line.warehouseId,
    isExtra: line.isExtra,
    isStockedLeaf: line.isStockedLeaf,
    canBeNegative: line.canBeNegative,
    available: null,
    unitCost: line.unitCost ?? 0,
    sourcePath: line.sourcePath,
    notes: null,
    stepNo: line.stepNo ?? null,
    trackLots: line.trackLots,
    trackExpiry: line.trackExpiry,
    shelfLifeDays: line.shelfLifeDays ?? null,
    lotSelection: (line.lots ?? []).some((lot) => lot.lotId)
      ? {
        mode: 'manual',
        lots: (line.lots ?? []).filter((lot) => lot.lotId).map((lot) => ({ lotId: lot.lotId as string, quantity: lot.quantity })),
      }
      : emptyLotSelection(),
    lotNoById: Object.fromEntries((line.lots ?? []).filter((lot) => lot.lotId).map((lot) => [lot.lotId as string, lot.lotNo || ''])),
  }));
}

function outputsFromOrder(order: ProductionOrder): ProductionExtraOutputRow[] {
  return (order.outputs ?? [])
    .filter((line) => isExtraOutputType(line.outputType))
    .map((line) => ({
      key: line.id,
      outputType: line.outputType as ProductionExtraOutputType,
      productId: line.itemId,
      productName: line.itemName,
      stockCode: line.stockCode,
      unit: line.unit,
      quantity: line.quantity,
      warehouseId: line.warehouseId,
      costMethod: costMethodOf(line.outputType, line.costMethod),
      costSharePercent: line.costSharePercent ?? null,
      fixedUnitValue: line.fixedUnitValue ?? null,
      unitCost: line.unitCost ?? null,
      allocatedCost: line.allocatedCost ?? null,
      trackLots: line.trackLots,
      trackExpiry: line.trackExpiry,
      shelfLifeDays: line.shelfLifeDays ?? null,
      lotNo: line.lotNo ?? '',
      expiryDate: line.expiryDate?.slice(0, 10) ?? '',
    }));
}

function outputsFromPreview(result: ProductionPreview, warehouseId: string): ProductionExtraOutputRow[] {
  return (result.extraOutputs ?? [])
    .filter((line) => isExtraOutputType(line.outputType))
    .map((line, index) => ({
      key: `${line.productId}-out-${index}`,
      outputType: line.outputType as ProductionExtraOutputType,
      productId: line.productId,
      productName: line.productName,
      stockCode: line.stockCode,
      unit: line.unit,
      quantity: line.quantity,
      warehouseId,
      costMethod: costMethodOf(line.outputType, line.costMethod),
      costSharePercent: line.costSharePercent ?? null,
      fixedUnitValue: line.fixedUnitValue ?? null,
      trackLots: line.trackLots,
      trackExpiry: line.trackExpiry,
      shelfLifeDays: line.shelfLifeDays ?? null,
      lotNo: '',
      expiryDate: '',
    }));
}

function rowsFromPreview(result: ProductionPreview, warehouseId: string): ProductionInputRow[] {
  return result.lines.map((line, index) => ({
    key: `${line.productId}-${index}`,
    productId: line.productId,
    productName: line.productName,
    stockCode: line.stockCode,
    unit: line.unit,
    entryUnit: line.entryUnit ?? null,
    entryFactor: line.entryFactor ?? null,
    plannedQuantity: line.plannedQuantity,
    actualQuantity: line.plannedQuantity,
    warehouseId,
    isExtra: false,
    isStockedLeaf: line.isStockedLeaf,
    canBeNegative: line.canBeNegative,
    available: line.available,
    unitCost: line.unitCost,
    sourcePath: line.sourcePath,
    notes: null,
    stepNo: line.stepNo ?? null,
    trackLots: line.trackLots,
    trackExpiry: line.trackExpiry,
    shelfLifeDays: line.shelfLifeDays ?? null,
    lotSelection: emptyLotSelection(),
    proposedLots: (line.proposedLots ?? []).map((lot) => ({ lotNo: lot.lotNo, quantity: lot.quantity, expired: lot.expired })),
    lotShortage: line.lotShortage ?? null,
  }));
}

function mergeAvailable(rows: ProductionInputRow[], result: ProductionPreview): ProductionInputRow[] {
  const byProduct = new Map(result.lines.map((line) => [line.productId, line]));
  return rows.map((row) => {
    const line = byProduct.get(row.productId);
    if (!line) return row;
    return {
      ...row,
      available: line.available,
      canBeNegative: line.canBeNegative,
      trackLots: line.trackLots ?? row.trackLots,
      trackExpiry: line.trackExpiry ?? row.trackExpiry,
      shelfLifeDays: line.shelfLifeDays ?? row.shelfLifeDays,
      proposedLots: (line.proposedLots ?? []).map((lot) => ({ lotNo: lot.lotNo, quantity: lot.quantity, expired: lot.expired })),
      lotShortage: line.lotShortage ?? null,
    };
  });
}

function isCustom(rows: ProductionInputRow[], result: ProductionPreview, warehouseId: string): boolean {
  if (rows.some((row) => row.isExtra)) return true;
  if (rows.length !== result.lines.length) return true;
  const byProduct = new Map(result.lines.map((line) => [line.productId, line]));
  return rows.some((row) => {
    const line = byProduct.get(row.productId);
    if (!line) return true;
    const rowEntry = row.entryUnit && !sameUnitName(row.entryUnit, row.unit) ? row.entryUnit : '';
    const lineEntry = line.entryUnit && !sameUnitName(line.entryUnit, line.unit) ? line.entryUnit : '';
    return Math.abs(row.plannedQuantity - line.plannedQuantity) > 1e-6
      || Math.abs(row.actualQuantity - line.plannedQuantity) > 1e-6
      || row.warehouseId !== warehouseId
      || (row.stepNo ?? null) !== (line.stepNo ?? null)
      || !sameUnitName(rowEntry || row.unit, lineEntry || line.unit)
      || (rowEntry !== '' && Math.abs((row.entryFactor ?? 1) - (line.entryFactor ?? 1)) > 1e-9);
  });
}

function describeError(
  error: unknown,
  translate: (key: string, options?: { defaultValue?: string }) => string,
): FormBanner & { code?: string; shortages?: ProductionStockShortage[] } {
  if (error instanceof ProductionApiError) {
    const translated = error.code ? translate(`errors.${error.code}`, { defaultValue: '' }) : '';
    return {
      severity: 'error',
      message: translated || error.message,
      detail: translated && error.message && error.message !== translated ? error.message : undefined,
      code: error.code,
      shortages: error.shortages,
    };
  }
  return { severity: 'error', message: translate('errors.VALIDATION_ERROR') };
}

export function useProductionOrderForm(orderId?: string) {
  const { t } = useTranslation('production');
  const { isModuleEnabled } = useAuth();
  const lotsEnabled = isModuleEnabled('lot_tracking');
  const opsEnabled = isModuleEnabled('production_ops');
  const nav = useProductionNav();
  const { branchId: preferredBranchId } = useProductionPermissions();
  const location = useLocation();
  const locationState = useRef(location.state);
  locationState.current = location.state;
  const [loading, setLoading] = useState(Boolean(orderId));
  const [pending, setPending] = useState(false);
  const [moduleDisabled, setModuleDisabled] = useState(false);
  const [banner, setBanner] = useState<FormBanner | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [product, setProductState] = useState<ProducibleProduct | null>(null);
  const [plannedQuantity, setPlannedState] = useState<number | null>(null);
  const [quantityMode, setQuantityModeState] = useState<ProductionQuantityMode>('total');
  const [baseQuantity, setBaseQuantityState] = useState<number | null>(null);
  const [warehouses, setWarehouses] = useState<ProductionWarehouse[]>([]);
  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [inputWarehouseId, setInputWarehouseId] = useState('');
  const [outputWarehouseId, setOutputWarehouseId] = useState('');
  const [separateOutput, setSeparateOutputState] = useState(false);
  const [productionDate, setProductionDateState] = useState<Date | null>(() => new Date());
  const [notes, setNotesState] = useState('');
  const [inputs, setInputs] = useState<ProductionInputRow[]>([]);
  const [outputs, setOutputs] = useState<ProductionExtraOutputRow[]>([]);
  const [mainLot, setMainLot] = useState({ trackLots: false, trackExpiry: false, shelfLifeDays: null as number | null, lotNo: '', expiryDate: '' });
  const [standardUnitCost, setStandardUnitCost] = useState<number | null>(null);
  const [currencyWarning, setCurrencyWarning] = useState(false);
  const [childPlan, setChildPlan] = useState<ProductionChildPlanNode[]>([]);
  const [childPlanWarnings, setChildPlanWarnings] = useState<string[]>([]);
  const [createChildOrders, setCreateChildOrdersState] = useState(true);
  const [linesCustomized, setLinesCustomized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [picker, setPicker] = useState<PickerKind | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState<ProductionStockShortage[]>([]);
  const [shortageDetail, setShortageDetail] = useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [recalcOpen, setRecalcOpen] = useState(false);
  const [movements, setMovements] = useState<ProductionMovement[]>([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [routingChoice, setRoutingChoice] = useState<boolean | null>(null);
  const [routingOffered, setRoutingOffered] = useState(false);
  const [routingStepCount, setRoutingStepCount] = useState(0);
  const [previewSteps, setPreviewSteps] = useState<ProductionPreviewStep[]>([]);
  const generation = useRef(0);
  const outputsCustomized = useRef(false);
  const committed = useRef<Trigger>({ product: null, qty: null, warehouseId: '', ops: false, routing: null, mode: 'total', base: null });
  const prefilled = useRef(false);
  const consumedFlash = useRef<string | null>(null);

  const lockedStatus = order?.status === 'COMPLETED'
    || order?.status === 'CANCELLED'
    || order?.status === 'RELEASED'
    || order?.status === 'IN_PROGRESS';
  const mode: ProductionEditorMode = !orderId ? 'new' : lockedStatus ? 'readonly' : 'draft';
  const editable = mode !== 'readonly';

  const applyPreviewMeta = (result: ProductionPreview) => {
    setStandardUnitCost(result.standardUnitCost);
    setCurrencyWarning(result.currencyWarning);
    setChildPlan(result.childPlan ?? []);
    setChildPlanWarnings(result.childPlanWarnings ?? []);
    setMainLot((current) => ({
      ...current,
      trackLots: result.mainTrackLots === true,
      trackExpiry: result.mainTrackExpiry === true,
      shelfLifeDays: result.mainShelfLifeDays ?? null,
      expiryDate: current.expiryDate || suggestExpiry(productionDate, result.mainShelfLifeDays),
    }));
  };

  const report = useCallback((error: unknown) => {
    if (error instanceof ProductionApiError && error.code === 'PRODUCTION_MODULE_DISABLED') {
      setModuleDisabled(true);
      return;
    }
    if (error instanceof ProductionApiError && (
      error.code === 'INSUFFICIENT_STOCK'
      || error.code === 'CANCEL_STOCK_CONSUMED'
      || error.code === 'CANCEL_LOT_CONSUMED'
      || error.code === 'LOT_INSUFFICIENT'
      || error.code === 'LOT_ON_HOLD'
      || error.code === 'LOT_EXPIRED'
    )) {
      setShortages(error.shortages ?? []);
      const extra = error.code === 'CANCEL_LOT_CONSUMED' ? ` ${t('lots.cancelNext')}` : '';
      setShortageDetail(`${error.message}${extra}`);
      setShortageOpen(true);
      setCompleteOpen(false);
      setCancelOpen(false);
      return;
    }
    const described = describeError(error, t);
    if (error instanceof ProductionApiError && error.code === 'INVALID_STATUS') {
      setBanner({ ...described, severity: 'warning' });
      return 'reload' as const;
    }
    setBanner(described);
    return undefined;
  }, [t]);

  const replacePreview = useCallback(async (trigger?: Partial<Trigger>, routingOverride?: boolean | null) => {
    const currentProduct = trigger?.product ?? product;
    const qty = trigger?.qty ?? plannedQuantity;
    const warehouseId = trigger?.warehouseId ?? inputWarehouseId;
    const currentMode = trigger?.mode ?? quantityMode;
    const base = trigger?.base !== undefined ? trigger.base : baseQuantity;
    const byBase = usesBase(currentProduct, currentMode);
    if (!currentProduct || !warehouseId || !outputWarehouseId) return;
    if (byBase ? (base == null || base <= 0) : (qty == null || qty <= 0)) return;
    const flag = routingOverride !== undefined ? routingOverride : routingChoice;
    const token = ++generation.current;
    try {
      const result = await preview({
        ProductId: currentProduct.productId,
        // Ana malzeme modunda planlanan miktarı sunucu hesaplar.
        PlannedQuantity: byBase ? 0 : qty!,
        InputWarehouseId: warehouseId,
        OutputWarehouseId: separateOutput ? outputWarehouseId : warehouseId,
        IncludeChildPlan: true,
        ...(opsEnabled && flag != null ? { UseRouting: flag } : {}),
        ...(byBase ? { BaseIngredientId: currentProduct.baseIngredientId, BaseQuantity: base } : {}),
      });
      if (token !== generation.current) return;
      const plannedResult = byBase ? (result.plannedQuantity ?? null) : qty;
      if (byBase) setPlannedState(plannedResult);
      const steps = result.steps ?? [];
      setPreviewSteps(flag === false ? [] : steps);
      if (flag !== false && steps.length > 0) {
        setRoutingOffered(true);
        setRoutingStepCount(steps.length);
        if (flag == null) setRoutingChoice(true);
      }
      setInputs(rowsFromPreview(result, warehouseId));
      if (!outputsCustomized.current) {
        setOutputs(outputsFromPreview(result, separateOutput ? outputWarehouseId : warehouseId));
      }
      applyPreviewMeta(result);
      setLinesCustomized(false);
      committed.current = {
        product: currentProduct,
        qty: plannedResult,
        warehouseId,
        ops: opsEnabled,
        routing: flag == null && steps.length > 0 ? true : flag,
        mode: byBase ? 'base' : 'total',
        base: byBase ? base : null,
      };
      setBanner(null);
    } catch (error) {
      if (token === generation.current) report(error);
    }
  }, [product, plannedQuantity, inputWarehouseId, outputWarehouseId, separateOutput, report, routingChoice, opsEnabled, quantityMode, baseQuantity]);

  useEffect(() => {
    let active = true;
    getWarehouses()
      .then((list) => {
        if (!active) return;
        setWarehouses(list);
        const preferred = preferredBranchId
          ? list.find((warehouse) => warehouse.branchId === preferredBranchId)?.id
          : undefined;
        const fallback = preferred || list.find((warehouse) => warehouse.isDefault)?.id || list[0]?.id || '';
        setInputWarehouseId((current) => current || fallback);
        setOutputWarehouseId((current) => current || fallback);
      })
      .catch(() => {
        if (active) setWarehouses([]);
      });
    accountingApi.get(ACCOUNTING_API.INVENTORY_CATEGORIES)
      .then((response) => {
        const data = response.data as InventoryCategory[] | { data?: InventoryCategory[] };
        const list = Array.isArray(data) ? data : (data?.data ?? []);
        if (active) setCategories(list);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
  }, [preferredBranchId]);

  useEffect(() => {
    if (orderId || prefilled.current || warehouses.length === 0) return;
    prefilled.current = true;
    const params = nav.newParams;
    if (!params) return;
    if (params.quantity != null) setPlannedState(params.quantity);
    if (params.warehouseId) {
      setInputWarehouseId(params.warehouseId);
      setOutputWarehouseId(params.warehouseId);
    }
  }, [orderId, warehouses, nav.newParams]);

  const applyInitialProduct = useCallback((next: ProducibleProduct) => {
    setProductState(next);
    // Yönlendirmeyle miktar geldiyse toplam üretime göre kalınır.
    if (next.baseIngredientId && nav.newParams?.quantity == null) {
      setQuantityModeState('base');
      setBaseQuantityState(null);
    }
  }, [nav.newParams]);

  const hydrate = useCallback((next: ProductionOrder) => {
    const nextProduct = productFromOrder(next);
    setOrder(next);
    setProductState(nextProduct);
    setPlannedState(next.plannedQuantity);
    // Mod fişe kaydedilmez: kayıtlı fiş toplam üretime göre açılır.
    setQuantityModeState('total');
    setBaseQuantityState(null);
    setInputWarehouseId(next.inputWarehouseId);
    setOutputWarehouseId(next.outputWarehouseId);
    setSeparateOutputState(next.inputWarehouseId !== next.outputWarehouseId);
    setProductionDateState(parseCalendarDate(next.productionDate) ?? new Date());
    setNotesState(next.notes ?? '');
    setInputs(rowsFromOrder(next));
    setOutputs(outputsFromOrder(next));
    const mainOutput = (next.outputs ?? []).find((line) => line.outputType === 'MAIN');
    const productionDay = parseCalendarDate(next.productionDate);
    setMainLot({
      trackLots: mainOutput?.trackLots === true,
      trackExpiry: mainOutput?.trackExpiry === true,
      shelfLifeDays: mainOutput?.shelfLifeDays ?? null,
      lotNo: mainOutput?.lotNo ?? '',
      expiryDate: mainOutput?.expiryDate?.slice(0, 10) || suggestExpiry(productionDay, mainOutput?.shelfLifeDays),
    });
    outputsCustomized.current = true;
    setStandardUnitCost(next.standardUnitCost ?? null);
    setRoutingChoice(next.hasRouting);
    setRoutingOffered(next.hasRouting);
    setRoutingStepCount(next.stepSummary?.total ?? 0);
    setPreviewSteps([]);
    committed.current = {
      product: nextProduct,
      qty: next.plannedQuantity,
      warehouseId: next.inputWarehouseId,
      ops: opsEnabled,
      routing: next.hasRouting,
      mode: 'total',
      base: null,
    };
  }, [opsEnabled]);

  // Taslakta ana malzeme seçeneğini sunabilmek için ürünün reçete bilgisi tamamlanır.
  const draftProductId = order?.status === 'DRAFT' ? order.productId : null;
  useEffect(() => {
    if (!draftProductId) return undefined;
    let active = true;
    getProducibleProducts({ productId: draftProductId, page: 1, pageSize: 1 })
      .then((page) => {
        const match = page.items?.find((row) => row.productId === draftProductId);
        if (!active || !match || (!match.baseIngredientId && !match.isVariableOutput)) return;
        setProductState((current) => (current && current.productId === match.productId
          ? {
            ...current,
            baseIngredientId: match.baseIngredientId,
            baseIngredientName: match.baseIngredientName,
            baseIngredientUnit: match.baseIngredientUnit,
            baseIngredientBatchQuantity: match.baseIngredientBatchQuantity,
            isVariableOutput: match.isVariableOutput,
            expectedOutputMin: match.expectedOutputMin,
            expectedOutputMax: match.expectedOutputMax,
            recipeOutputQuantity: match.recipeOutputQuantity || current.recipeOutputQuantity,
          }
          : current));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [draftProductId, order]);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      setOrder(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setBanner(null);
    setMovements([]);
    const token = ++generation.current;
    getOrder(orderId)
      .then(async (loaded) => {
        if (!active) return;
        hydrate(loaded);
        if (loaded.status === 'DRAFT' && loaded.plannedQuantity > 0) {
          try {
            const result = await preview({
              ProductId: loaded.productId,
              PlannedQuantity: loaded.plannedQuantity,
              InputWarehouseId: loaded.inputWarehouseId,
              OutputWarehouseId: loaded.outputWarehouseId,
              IncludeChildPlan: true,
              ...(opsEnabled ? { UseRouting: loaded.hasRouting } : {}),
            });
            if (!active || token !== generation.current) return;
            const saved = rowsFromOrder(loaded);
            setInputs(mergeAvailable(saved, result));
            applyPreviewMeta(result);
            setLinesCustomized(isCustom(saved, result, loaded.inputWarehouseId));
            if (loaded.hasRouting) {
              const steps = result.steps ?? [];
              setPreviewSteps(steps);
              setRoutingOffered(steps.length > 0 || loaded.hasRouting);
              setRoutingStepCount(steps.length || loaded.stepSummary?.total || 0);
            } else if (opsEnabled) {
              try {
                const probe = await preview({
                  ProductId: loaded.productId,
                  PlannedQuantity: loaded.plannedQuantity,
                  InputWarehouseId: loaded.inputWarehouseId,
                  OutputWarehouseId: loaded.outputWarehouseId,
                  IncludeChildPlan: false,
                  UseRouting: true,
                });
                if (!active || token !== generation.current) return;
                const steps = probe.steps ?? [];
                if (steps.length > 0) {
                  setRoutingOffered(true);
                  setRoutingStepCount(steps.length);
                }
              } catch {
                // Anahtar, rota yoksa gizli kalır.
              }
            }
          } catch (error) {
            if (active) report(error);
          }
        }
        if (!active) return;
        setDirty(false);
        const flash = locationState.current as ProductionOrderNavigationState | null;
        if (flash && consumedFlash.current !== loaded.id) {
          consumedFlash.current = loaded.id;
          if (flash.message) setSnack(flash.message);
          if (flash.openComplete && loaded.status === 'DRAFT') setCompleteOpen(true);
        }
      })
      .catch((error) => {
        if (active) report(error);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderId, hydrate, report, opsEnabled]);

  useEffect(() => {
    if (!editable || loading) return undefined;
    const byBase = usesBase(product, quantityMode);
    if (!product || !inputWarehouseId) return undefined;
    if (byBase ? (baseQuantity == null || baseQuantity <= 0) : (plannedQuantity == null || plannedQuantity <= 0)) return undefined;
    const timer = window.setTimeout(() => {
      const sameQuantity = byBase
        ? committed.current.mode === 'base' && committed.current.base === baseQuantity
        : committed.current.qty === plannedQuantity;
      const same = committed.current.product?.productId === product.productId
        && sameQuantity
        && committed.current.warehouseId === inputWarehouseId
        && committed.current.ops === opsEnabled
        && committed.current.routing === routingChoice;
      if (same) return;
      if (linesCustomized) {
        setRecalcOpen(true);
        return;
      }
      void replacePreview({ product, qty: plannedQuantity, warehouseId: inputWarehouseId, mode: quantityMode, base: baseQuantity });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [product, plannedQuantity, inputWarehouseId, editable, loading, linesCustomized, replacePreview, opsEnabled, routingChoice, quantityMode, baseQuantity]);

  useEffect(() => {
    if (!dirty || !editable) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, editable]);

  const touch = () => setDirty(true);

  const setProduct = (next: ProducibleProduct | null) => {
    setProductState(next);
    // Reçetesinde ana malzeme olan üründe varsayılan: ana malzemeye göre.
    if (next?.baseIngredientId) {
      setQuantityModeState('base');
      setBaseQuantityState(null);
      setPlannedState(null);
    } else {
      setQuantityModeState('total');
      setBaseQuantityState(null);
    }
    touch();
    setRoutingChoice(null);
    setRoutingOffered(false);
    setRoutingStepCount(0);
    setPreviewSteps([]);
    outputsCustomized.current = false;
    setOutputs([]);
    if (!next) {
      setInputs([]);
      setChildPlan([]);
      setChildPlanWarnings([]);
    }
  };

  const setPlannedQuantity = (value: number | null) => {
    setPlannedState(value);
    touch();
  };

  const setBaseQuantity = (value: number | null) => {
    setBaseQuantityState(value);
    touch();
  };

  const setQuantityMode = (next: ProductionQuantityMode) => {
    if (next === quantityMode) return;
    setQuantityModeState(next);
    if (next === 'base') {
      // Mevcut satırdaki ana malzeme miktarıyla başlanır.
      const baseId = product?.baseIngredientId;
      const current = baseId
        ? inputs.filter((row) => row.productId === baseId && !row.isExtra).reduce((sum, row) => sum + row.plannedQuantity, 0)
        : 0;
      setBaseQuantityState(current > 0 ? Math.round(current * 1_000_000) / 1_000_000 : null);
    } else if (committed.current.mode === 'base') {
      // Satırlar zaten hesaplı; toplam moduna geçmek yeniden hesap gerektirmez.
      committed.current = { ...committed.current, mode: 'total', base: null };
    }
    touch();
  };

  const setInputWarehouse = (id: string) => {
    setInputWarehouseId(id);
    if (!separateOutput) setOutputWarehouseId(id);
    touch();
  };

  const setOutputWarehouse = (id: string) => {
    setOutputWarehouseId(id);
    touch();
  };

  const setSeparateOutput = (value: boolean) => {
    setSeparateOutputState(value);
    if (!value) setOutputWarehouseId(inputWarehouseId);
    touch();
  };

  const setProductionDate = (value: Date | null) => {
    setProductionDateState(value);
    touch();
  };

  const setNotes = (value: string) => {
    setNotesState(value.slice(0, 500));
    touch();
  };

  const setCreateChildOrders = (value: boolean) => {
    setCreateChildOrdersState(value);
    touch();
  };

  const setInputActual = (key: string, displayValue: number) => {
    setInputs((current) => current.map((row) => (
      row.key === key ? { ...row, actualQuantity: toBaseQty(displayValue, row.entryFactor) } : row
    )));
    setLinesCustomized(true);
    touch();
  };

  const setInputStep = (key: string, stepNo: number) => {
    setInputs((current) => current.map((row) => (row.key === key ? { ...row, stepNo } : row)));
    setLinesCustomized(true);
    touch();
  };

  const setUseRouting = (value: boolean) => {
    setRoutingChoice(value);
    setLinesCustomized(false);
    touch();
    void replacePreview(undefined, value);
  };

  const setInputUnit = (key: string, entryUnit: string | null, entryFactor: number | null) => {
    setInputs((current) => current.map((row) => (
      row.key === key ? { ...row, entryUnit, entryFactor } : row
    )));
    setLinesCustomized(true);
    touch();
  };

  const setInputWarehouseRow = (key: string, warehouseId: string) => {
    const row = inputs.find((item) => item.key === key);
    setInputs((current) => current.map((item) => (item.key === key ? { ...item, warehouseId, available: null } : item)));
    setLinesCustomized(true);
    touch();
    if (!row || !product || plannedQuantity == null || plannedQuantity <= 0 || !outputWarehouseId) return;
    const token = generation.current;
    preview({
      ProductId: product.productId,
      PlannedQuantity: plannedQuantity,
      InputWarehouseId: warehouseId,
      OutputWarehouseId: separateOutput ? outputWarehouseId : inputWarehouseId,
      IncludeChildPlan: false,
      ...(opsEnabled && routingChoice != null ? { UseRouting: routingChoice } : {}),
    }).then((result) => {
      if (token !== generation.current) return;
      const match = result.lines.find((line) => line.productId === row.productId && (line.stepNo ?? null) === (row.stepNo ?? null))
        ?? result.lines.find((line) => line.productId === row.productId);
      if (!match) return;
      setInputs((current) => current.map((item) => (
        item.key === key ? { ...item, available: match.available, canBeNegative: match.canBeNegative } : item
      )));
    }).catch(() => undefined);
  };

  const removeInput = (key: string) => {
    setInputs((current) => current.filter((row) => row.key !== key));
    setLinesCustomized(true);
    touch();
  };

  const addInputs = (items: InventoryItem[]) => {
    setInputs((current) => [
      ...current,
      ...items.map((item) => ({
        key: crypto.randomUUID(),
        productId: item.id,
        productName: item.name,
        stockCode: item.stockCode,
        unit: item.unit,
        entryUnit: null,
        entryFactor: null,
        plannedQuantity: 0,
        actualQuantity: 1,
        warehouseId: inputWarehouseId,
        isExtra: true,
        isStockedLeaf: false,
        canBeNegative: item.canBeNegative ?? false,
        available: null,
        unitCost: item.purchasePrice ?? 0,
        notes: null,
        stepNo: routingChoice === true && previewSteps[0] ? previewSteps[0].stepNo : null,
        trackLots: item.trackLots,
        trackExpiry: item.trackExpiry,
        shelfLifeDays: item.shelfLifeDays ?? null,
        lotSelection: emptyLotSelection(),
      })),
    ]);
    setLinesCustomized(true);
    touch();
  };

  const markOutputs = () => {
    outputsCustomized.current = true;
    touch();
  };

  const setOutputQuantity = (key: string, value: number) => {
    setOutputs((current) => current.map((row) => (row.key === key ? { ...row, quantity: value } : row)));
    markOutputs();
  };

  const setOutputWarehouseRow = (key: string, warehouseId: string) => {
    setOutputs((current) => current.map((row) => (row.key === key ? { ...row, warehouseId } : row)));
    markOutputs();
  };

  const setOutputCost = (key: string, patch: Partial<Pick<ProductionExtraOutputRow, 'costMethod' | 'costSharePercent' | 'fixedUnitValue'>>) => {
    setOutputs((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    markOutputs();
  };

  const removeOutput = (key: string) => {
    setOutputs((current) => current.filter((row) => row.key !== key));
    markOutputs();
  };

  const addOutputs = (outputType: ProductionExtraOutputType, items: InventoryItem[]) => {
    setOutputs((current) => [
      ...current,
      ...items.map((item) => ({
        key: crypto.randomUUID(),
        outputType,
        productId: item.id,
        productName: item.name,
        stockCode: item.stockCode,
        unit: item.unit,
        quantity: 1,
        warehouseId: outputWarehouseId || inputWarehouseId,
        costMethod: costMethodOf(outputType, null),
        costSharePercent: null,
        fixedUnitValue: null,
        trackLots: item.trackLots,
        trackExpiry: item.trackExpiry,
        shelfLifeDays: item.shelfLifeDays ?? null,
        lotNo: '',
        expiryDate: suggestExpiry(productionDate, item.shelfLifeDays),
      })),
    ]);
    markOutputs();
  };

  const validate = (): string | null => {
    if (!product || plannedQuantity == null || plannedQuantity <= 0 || !inputWarehouseId || !outputWarehouseId) {
      return t('editor.validation');
    }
    // Ana malzeme miktarı değişti ama planlanan miktar henüz hesaplanmadı.
    if (usesBase(product, quantityMode) && (committed.current.mode !== 'base' || committed.current.base !== baseQuantity)) {
      return t('editor.baseNotCalculated');
    }
    if (inputs.some((row) => row.actualQuantity < 0) || outputs.some((row) => row.quantity <= 0)) {
      return t('editor.validation');
    }
    const mismatched = inputs.find((row) => row.trackLots && lotSelectionMismatch(row.lotSelection, row.actualQuantity));
    if (mismatched) return t('lots.mismatch', { name: mismatched.productName });
    return null;
  };

  const buildBody = (withChildren: boolean): CreateProductionOrderPayload => ({
    ProductId: product!.productId,
    PlannedQuantity: plannedQuantity!,
    InputWarehouseId: inputWarehouseId,
    OutputWarehouseId: separateOutput ? outputWarehouseId : inputWarehouseId,
    ProductionDate: toCalendarDate(productionDate) ?? null,
    Notes: notes.trim() || null,
    ...(opsEnabled && routingChoice != null ? { UseRouting: routingChoice } : {}),
    Inputs: (routingChoice === true
      ? [...inputs].sort((left, right) => (left.stepNo ?? 0) - (right.stepNo ?? 0))
      : inputs).map((row) => {
      const quantities = entryLineQuantities(row);
      return {
        ProductId: row.productId,
        PlannedQuantity: quantities.PlannedQuantity,
        ActualQuantity: quantities.ActualQuantity,
        WarehouseId: row.warehouseId,
        IsExtra: row.isExtra,
        Notes: row.notes ?? null,
        EntryUnit: quantities.EntryUnit,
        ...(routingChoice === true && row.stepNo != null ? { StepNo: row.stepNo } : {}),
        Lots: lotsEnabled && row.trackLots ? payloadLots(row.lotSelection) : null,
      };
    }),
    ExtraOutputs: outputs.map((row) => ({
      OutputType: row.outputType,
      ProductId: row.productId,
      Quantity: row.quantity,
      WarehouseId: row.warehouseId,
      CostMethod: row.costMethod,
      CostSharePercent: row.costMethod === 'SHARE' ? row.costSharePercent : null,
      FixedUnitValue: row.costMethod === 'FIXED' ? row.fixedUnitValue : null,
      ...(lotsEnabled && row.trackLots ? { LotNo: row.lotNo?.trim() || null, ExpiryDate: row.expiryDate || null } : {}),
    })),
    CreateChildOrders: withChildren,
    ...(lotsEnabled && mainLot.trackLots ? { MainLotNo: mainLot.lotNo.trim(), MainExpiryDate: mainLot.expiryDate || null } : {}),
  });

  const reload = useCallback(() => {
    if (!orderId) return;
    setLoading(true);
    getOrder(orderId)
      .then((loaded) => {
        hydrate(loaded);
        setDirty(false);
      })
      .catch((error) => report(error))
      .finally(() => setLoading(false));
  }, [orderId, hydrate, report]);

  const run = async (work: () => Promise<void>) => {
    if (pending) return;
    setPending(true);
    setBanner(null);
    try {
      await work();
    } catch (error) {
      if (report(error) === 'reload') reload();
    } finally {
      setPending(false);
    }
  };

  const saveDraft = () => run(async () => {
    const invalid = validate();
    if (invalid) {
      setBanner({ severity: 'error', message: invalid });
      return;
    }
    const created = await createOrder(buildBody(createChildOrders));
    queueProductionOrderNavigation({ message: t('editor.saved', { number: created.orderNumber }) });
    setDirty(false);
    nav.openOrder(created.id);
  });

  const save = () => run(async () => {
    if (!order) return;
    const invalid = validate();
    if (invalid) {
      setBanner({ severity: 'error', message: invalid });
      return;
    }
    const updated = await updateOrder(order.id, buildBody(false));
    hydrate(updated);
    setDirty(false);
    setSnack(t('editor.updated'));
  });

  const release = () => run(async () => {
    const invalid = validate();
    if (invalid) {
      setBanner({ severity: 'error', message: invalid });
      return;
    }
    let current = order;
    const created = !current;
    if (!current) {
      current = await createOrder(buildBody(createChildOrders));
    } else if (dirty) {
      current = await updateOrder(current.id, buildBody(false));
      hydrate(current);
      setDirty(false);
    }
    let released: ProductionOrder;
    try {
      released = await releaseOrder(current.id);
    } catch (error) {
      if (created) {
        const message = error instanceof ProductionApiError ? error.message : t('errors.VALIDATION_ERROR');
        queueProductionOrderNavigation({ message });
        setDirty(false);
        nav.openOrder(current.id);
        return;
      }
      throw error;
    }
    setDirty(false);
    if (!orderId || orderId !== released.id) {
      queueProductionOrderNavigation({ message: t('ops.released') });
      nav.openOrder(released.id);
      return;
    }
    hydrate(released);
    setSnack(t('ops.released'));
  });

  const unrelease = () => run(async () => {
    if (!order) return;
    const draft = await unreleaseOrder(order.id);
    hydrate(draft);
    setDirty(false);
    setSnack(t('ops.unreleased'));
  });

  const refreshOrder = useCallback(() => {
    if (!orderId) return;
    getOrder(orderId)
      .then((loaded) => {
        hydrate(loaded);
        setDirty(false);
      })
      .catch((error) => report(error));
  }, [orderId, hydrate, report]);

  const saveAndComplete = () => run(async () => {
    const invalid = validate();
    if (invalid) {
      setBanner({ severity: 'error', message: invalid });
      return;
    }
    const created = await createOrder(buildBody(createChildOrders));
    queueProductionOrderNavigation({ openComplete: true });
    setDirty(false);
    nav.openOrder(created.id);
  });

  const openComplete = () => run(async () => {
    if (dirty && order) {
      const invalid = validate();
      if (invalid) {
        setBanner({ severity: 'error', message: invalid });
        return;
      }
      const updated = await updateOrder(order.id, buildBody(false));
      hydrate(updated);
      setDirty(false);
    }
    setCompleteOpen(true);
  });

  const setInputLots = (key: string, selection: LotSelection, names: Record<string, string>) => {
    setInputs((current) => current.map((row) => (
      row.key === key ? { ...row, lotSelection: selection, lotNoById: { ...row.lotNoById, ...names } } : row
    )));
    touch();
  };

  const setOutputLot = (key: string, patch: { lotNo?: string; expiryDate?: string }) => {
    setOutputs((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    markOutputs();
  };

  const submitComplete = (body: {
    ProducedQuantity: number;
    ProductionDate: string | null;
    Inputs: CreateProductionOrderPayload['Inputs'];
    ExtraOutputs: CreateProductionOrderPayload['ExtraOutputs'];
    MainLotNo?: string | null;
    MainExpiryDate?: string | null;
    tree: boolean;
  }) => run(async () => {
    if (!order) return;
    const payload = {
      ProducedQuantity: body.ProducedQuantity,
      ProductionDate: body.ProductionDate,
      Inputs: body.Inputs,
      ExtraOutputs: body.ExtraOutputs,
      ...(body.MainLotNo !== undefined ? { MainLotNo: body.MainLotNo, MainExpiryDate: body.MainExpiryDate ?? null } : {}),
    };
    const completed = body.tree
      ? await completeTree(order.id, payload)
      : await completeOrder(order.id, payload);
    hydrate(completed);
    setDirty(false);
    setCompleteOpen(false);
    setSnack(t('complete.success'));
  });

  const submitCancel = (reason: string, tree: boolean) => run(async () => {
    if (!order) return;
    const cancelled = tree
      ? await cancelTree(order.id, { Reason: reason })
      : await cancelOrder(order.id, { Reason: reason });
    hydrate(cancelled);
    setDirty(false);
    setCancelOpen(false);
    setMovements([]);
    setSnack(t('cancel.success'));
  });

  const regenerate = () => run(async () => {
    if (!order) return;
    const refreshed = await regenerateChildren(order.id);
    hydrate(refreshed);
    setDirty(false);
    setShortageOpen(false);
    setSnack(t('editor.regenerated'));
  });

  const confirmDelete = () => run(async () => {
    if (!order) return;
    await deleteOrder(order.id);
    setDirty(false);
    setDeleteOpen(false);
    nav.openList();
  });

  const requestLeave = () => {
    if (dirty && editable) setLeaveOpen(true);
    else nav.openList();
  };

  const acceptRecalc = () => {
    setRecalcOpen(false);
    void replacePreview();
  };

  const rejectRecalc = () => {
    const previous = committed.current;
    setProductState(previous.product);
    setPlannedState(previous.qty);
    setQuantityModeState(previous.mode);
    setBaseQuantityState(previous.base);
    setInputWarehouseId(previous.warehouseId);
    if (!separateOutput) setOutputWarehouseId(previous.warehouseId);
    setRecalcOpen(false);
  };

  const loadMovements = useCallback(() => {
    if (!orderId) return;
    setMovementsLoading(true);
    getMovements(orderId)
      .then((rows) => setMovements(rows ?? []))
      .catch((error) => report(error))
      .finally(() => setMovementsLoading(false));
  }, [orderId, report]);

  const modalWarehouses: Warehouse[] = warehouses.map((warehouse) => ({
    id: warehouse.id,
    name: warehouse.name,
    isDefault: warehouse.isDefault,
    branchesId: warehouse.branchId,
  }));

  return {
    mode,
    loading,
    pending,
    moduleDisabled,
    banner,
    clearBanner: () => setBanner(null),
    snack,
    clearSnack: () => setSnack(null),
    order,
    product,
    setProduct,
    applyInitialProduct,
    plannedQuantity,
    setPlannedQuantity,
    quantityMode,
    setQuantityMode,
    baseQuantity,
    setBaseQuantity,
    warehouses,
    categories,
    modalWarehouses,
    inputWarehouseId,
    setInputWarehouse,
    outputWarehouseId,
    setOutputWarehouse,
    separateOutput,
    setSeparateOutput,
    productionDate,
    setProductionDate,
    notes,
    setNotes,
    inputs,
    lotsEnabled,
    mainLot,
    setMainLotNo: (lotNo: string) => { setMainLot((current) => ({ ...current, lotNo })); touch(); },
    setMainExpiry: (expiryDate: string) => { setMainLot((current) => ({ ...current, expiryDate })); touch(); },
    setInputLots,
    setInputActual,
    setInputUnit,
    setInputStep,
    useRouting: routingChoice,
    routingOffered,
    routingStepCount,
    previewSteps,
    setUseRouting,
    release,
    unrelease,
    refreshOrder,
    setInputWarehouseRow,
    removeInput,
    addInputs,
    outputs,
    setOutputLot,
    setOutputQuantity,
    setOutputWarehouseRow,
    setOutputCost,
    removeOutput,
    addOutputs,
    standardUnitCost,
    currencyWarning,
    childPlan,
    childPlanWarnings,
    createChildOrders,
    setCreateChildOrders,
    picker,
    openPicker: (kind: PickerKind) => setPicker(kind),
    closePicker: () => setPicker(null),
    calculate: () => {
      if (linesCustomized) setRecalcOpen(true);
      else void replacePreview();
    },
    saveDraft,
    save,
    saveAndComplete,
    openComplete,
    completeOpen,
    closeComplete: () => setCompleteOpen(false),
    submitComplete,
    cancelOpen,
    openCancel: () => setCancelOpen(true),
    closeCancel: () => setCancelOpen(false),
    submitCancel,
    parentCompleted: order?.parent?.status === 'COMPLETED',
    allowCancelTree: (order?.children ?? []).some((child) => child.status === 'COMPLETED'),
    shortageOpen,
    shortages,
    shortageDetail,
    closeShortage: () => setShortageOpen(false),
    regenerate,
    deleteOpen,
    requestDelete: () => setDeleteOpen(true),
    closeDelete: () => setDeleteOpen(false),
    confirmDelete,
    historyOpen,
    setHistoryOpen,
    leaveOpen,
    requestLeave,
    stay: () => setLeaveOpen(false),
    confirmLeave: () => {
      setLeaveOpen(false);
      setDirty(false);
      nav.openList();
    },
    recalcOpen,
    acceptRecalc,
    rejectRecalc,
    movements,
    movementsLoading,
    loadMovements,
    reload,
  };
}
